import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Card, CardContent, CardDescription, CardFooter, CardHeader, CardTitle } from './ui/card';
import { Button } from './ui/button';
import { Badge } from './ui/badge';
import { Check, Loader2, ArrowLeft, ArrowUp, Calendar, DollarSign } from 'lucide-react';
import { useToast } from '../hooks/use-toast';
import { useLanguage } from '@/contexts/LanguageContext';
import LanguageSelector from './LanguageSelector';

const Pricing = () => {
  const { t } = useLanguage();
  const navigate = useNavigate();
  const [loading, setLoading] = useState(false);
  const [subscriptionStatus, setSubscriptionStatus] = useState(null);
  const [packages, setPackages] = useState({});
  const [billingCycle, setBillingCycle] = useState('monthly');
  const [activeTab, setActiveTab] = useState('new'); // 'new' or 'upgrade'
  const [upgradeCalculation, setUpgradeCalculation] = useState(null);
  const [calculatingUpgrade, setCalculatingUpgrade] = useState(false);
  const [targetUpgradePackage, setTargetUpgradePackage] = useState(null);
  const { toast } = useToast();

  useEffect(() => {
    fetchSubscriptionStatus();
    fetchPackages();
  }, []);

  const fetchSubscriptionStatus = async () => {
    try {
      const response = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/subscription/status`, {
        headers: {
          'Authorization': `Bearer ${localStorage.getItem('token')}`
        }
      });
      
      if (response.ok) {
        const status = await response.json();
        setSubscriptionStatus(status);
      }
    } catch (error) {
      console.error('Failed to fetch subscription status:', error);
    }
  };

  const fetchPackages = async () => {
    try {
      const response = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/payments/packages`);
      if (response.ok) {
        const data = await response.json();
        setPackages(data);
      }
    } catch (error) {
      console.error('Failed to fetch packages:', error);
    }
  };


  const calculateUpgrade = async (targetPackageId) => {
    setCalculatingUpgrade(true);
    setTargetUpgradePackage(targetPackageId); // Store the target package
    try {
      const response = await fetch(
        `${process.env.REACT_APP_BACKEND_URL}/api/payments/upgrade-calculation?target_package_id=${targetPackageId}`,
        {
          headers: {
            'Authorization': `Bearer ${localStorage.getItem('token')}`
          }
        }
      );

      if (response.ok) {
        const calculation = await response.json();
        setUpgradeCalculation(calculation);
      } else {
        const error = await response.json();
        toast({
          title: "Cannot Calculate Upgrade",
          description: error.detail || "Unable to calculate upgrade cost",
          variant: "destructive"
        });
      }
    } catch (error) {
      console.error('Failed to calculate upgrade:', error);
      toast({
        title: "Error",
        description: "Failed to calculate upgrade cost",
        variant: "destructive"
      });
    } finally {
      setCalculatingUpgrade(false);
    }
  };

  const handleUpgrade = async (packageId) => {
    if (loading) return;
    
    setLoading(true);
    try {
      const response = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/payments/process-upgrade`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${localStorage.getItem('token')}`
        },
        body: JSON.stringify({
          target_package_id: packageId,
          origin_url: window.location.origin
        })
      });

      if (response.ok) {
        const data = await response.json();
        window.location.href = data.url;
      } else {
        const error = await response.json();
        toast({
          title: "Upgrade Failed",
          description: error.detail || "Failed to process upgrade",
          variant: "destructive"
        });
      }
    } catch (error) {
      console.error('Upgrade error:', error);
      toast({
        title: "Error",
        description: "An error occurred while processing your upgrade",
        variant: "destructive"
      });
    } finally {
      setLoading(false);
    }
  };


  const handleSubscribe = async (packageId) => {
    if (loading) return;
    
    setLoading(true);
    
    try {
      const response = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/payments/create-checkout`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${localStorage.getItem('token')}`
        },
        body: JSON.stringify({
          package_id: packageId,
          origin_url: window.location.origin,
          user_count: 1
        })
      });

      const data = await response.json();
      
      if (response.ok) {
        // Redirect to Stripe Checkout
        if (data.checkout_url) {
          window.location.href = data.checkout_url;
        } else {
          throw new Error('No checkout URL returned');
        }
      } else {
        setLoading(false);
        toast({
          title: "Error",
          description: data.detail || "Failed to initiate payment",
          variant: "destructive",
        });
      }
    } catch (error) {
      console.error('Payment error:', error);
      setLoading(false);
      toast({
        title: "Error",
        description: error.message || "Failed to initiate payment. Please try again.",
        variant: "destructive",
      });
    }
  };

  const calculatePrice = (basePrice) => {
    return (basePrice * 1).toFixed(2);
  };

  const getPlanPrice = (planId) => {
    // Use prices from backend packages
    const packageKey = billingCycle === 'yearly' ? `${planId}_yearly` : `${planId}_monthly`;
    const packageData = packages[packageKey];
    
    if (!packageData) {
      return '0.00';
    }
    
    if (billingCycle === 'yearly') {
      // Show monthly equivalent for yearly plans
      const monthlyEquivalent = packageData.amount / 12;
      return monthlyEquivalent.toFixed(2);
    }
    
    return packageData.amount.toFixed(2);
  };
  
  const getTotalYearlyPrice = (planId) => {
    const packageKey = `${planId}_yearly`;
    const packageData = packages[packageKey];
    return packageData ? packageData.amount.toFixed(2) : '0.00';
  };

  const plans = [
    {
      id: 'starter',
      name: t('starterPlan'),
      description: t('perfectForSmallTeams'),
      color: 'border-gray-200',
      popular: false,
      features: [
        t('maintenanceWorkOrders'),
        t('repairWorkOrders'),
        t('maintenanceHistoryFeature'),
        t('repairHistoryFeature'),
        t('emailSupport')
      ]
    },
    {
      id: 'professional',
      name: t('professionalPlan'),
      description: t('bestForGrowingBusinesses'),
      color: 'border-blue-500',
      popular: true,
      features: [
        t('maintenanceWorkOrders'),
        t('repairWorkOrders'),
        t('maintenanceHistoryFeature'),
        t('repairHistoryFeature'),
        t('inventoryTracking'),
        t('emailSupport')
      ]
    }
  ];

  // Check if user is logged in
  const isLoggedIn = localStorage.getItem('token') !== null;

  return (
    <div className="container mx-auto px-4 py-8">
      {/* Language Selector - Top Right */}
      <div className="absolute top-4 right-4">
        <LanguageSelector />
      </div>
      
      <div className="flex justify-between items-start mb-8">
        <Button
          variant="ghost"
          onClick={() => navigate(isLoggedIn ? '/' : '/login')}
          className="flex items-center text-gray-600 hover:text-gray-900"
        >
          <ArrowLeft className="w-4 h-4 mr-2" />
          {isLoggedIn ? t('backToDashboard') : t('backToLogin')}
        </Button>
      </div>
      <div className="text-center mb-8">
        <h1 className="text-4xl font-bold text-gray-900 mb-4">{t('chooseYourPlan')}</h1>
        <p className="text-xl text-gray-600 mb-6">
          {t('pricingSubtitle')}
        </p>
        
        {subscriptionStatus?.is_trial && (
          <div className="bg-blue-50 border border-blue-200 rounded-lg p-4 mb-6 inline-block">
            <div className="flex items-center gap-2">
              <Badge variant="secondary">{t('freeTrial')}</Badge>
              <span className="text-blue-700 font-medium">
                {subscriptionStatus.trial_days_remaining} {t('daysRemaining')}
              </span>
            </div>
          </div>
        )}
        
        {subscriptionStatus?.has_active_subscription && !subscriptionStatus?.is_trial && (
          <div className="bg-green-50 border border-green-200 rounded-lg p-4 mb-6 inline-block">
            <div className="flex items-center gap-2">
              <Badge variant="default" className="bg-green-600">{t('activeSubscription')}</Badge>
              <span className="text-green-700 font-medium">
                {subscriptionStatus.subscription_type}
              </span>
            </div>
          </div>
        )}

        {/* Tab System - Show upgrade tab only if user has active subscription */}
        {subscriptionStatus?.has_active_subscription && !subscriptionStatus?.is_trial && (
          <div className="flex justify-center gap-2 mb-6">
            <Button
              variant={activeTab === 'new' ? 'default' : 'outline'}
              onClick={() => setActiveTab('new')}
              className="min-w-[120px]"
            >
              {t('newPlan')}
            </Button>
            <Button
              variant={activeTab === 'upgrade' ? 'default' : 'outline'}
              onClick={() => {
                setActiveTab('upgrade');
                // Auto-calculate upgrade based on current plan
                if (subscriptionStatus?.subscription_type) {
                  const planType = subscriptionStatus.subscription_type.toLowerCase();
                  let targetPlan = '';
                  
                  // Determine target upgrade plan
                  if (planType.includes('starter')) {
                    // Starter users upgrade to Professional (same billing cycle)
                    targetPlan = billingCycle === 'monthly' ? 'professional_monthly' : 'professional_yearly';
                  } else if (planType.includes('professional') && planType.includes('monthly')) {
                    // Professional Monthly users upgrade to Professional Yearly
                    targetPlan = 'professional_yearly';
                    setBillingCycle('yearly'); // Switch to yearly tab
                  } else if (planType.includes('starter') && planType.includes('monthly')) {
                    // Starter Monthly users can upgrade to Starter Yearly or Professional
                    targetPlan = 'professional_monthly';
                  }
                  
                  if (targetPlan) {
                    calculateUpgrade(targetPlan);
                  }
                }
              }}
              className="min-w-[120px] flex items-center gap-2"
            >
              <ArrowUp className="w-4 h-4" />
              {t('upgradePlan')}
            </Button>
          </div>
        )}

        {/* Billing Toggle */}
        <div className="flex justify-center mb-8">
          <div className="bg-gray-100 rounded-lg p-1 flex">
            <Button
              variant={billingCycle === 'monthly' ? 'default' : 'ghost'}
              size="sm"
              onClick={() => {
                setBillingCycle('monthly');
                // Recalculate upgrade if on upgrade tab
                if (activeTab === 'upgrade' && subscriptionStatus?.subscription_type) {
                  const planType = subscriptionStatus.subscription_type.toLowerCase();
                  if (planType.includes('starter')) {
                    calculateUpgrade('professional_monthly');
                  }
                }
              }}
              className="rounded-md"
            >
              {t('monthly')}
            </Button>
            <Button
              variant={billingCycle === 'yearly' ? 'default' : 'ghost'}
              size="sm"
              onClick={() => {
                setBillingCycle('yearly');
                // Recalculate upgrade if on upgrade tab
                if (activeTab === 'upgrade' && subscriptionStatus?.subscription_type) {
                  const planType = subscriptionStatus.subscription_type.toLowerCase();
                  if (planType.includes('starter')) {
                    calculateUpgrade('professional_yearly');
                  } else if (planType.includes('professional') && planType.includes('monthly')) {
                    calculateUpgrade('professional_yearly');
                  }
                }
              }}
              className="rounded-md"
            >
              {t('yearly')}
            </Button>
          </div>
        </div>
      </div>

      {/* Upgrade Calculation Display */}
      {activeTab === 'upgrade' && upgradeCalculation && (
        <div className="max-w-3xl mx-auto mb-8">
          <Card className="border-blue-200 bg-gradient-to-br from-blue-50 to-indigo-50">
            <CardHeader>
              <CardTitle className="flex items-center gap-2 text-blue-900">
                <ArrowUp className="w-5 h-5" />
                Upgrade Calculation
              </CardTitle>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="grid grid-cols-2 gap-4">
                <div className="bg-white p-4 rounded-lg">
                  <p className="text-sm text-gray-600 mb-1">Current Plan</p>
                  <p className="font-semibold text-gray-900">{upgradeCalculation.current_plan}</p>
                </div>
                <div className="bg-white p-4 rounded-lg">
                  <p className="text-sm text-gray-600 mb-1">Upgrading To</p>
                  <p className="font-semibold text-green-700">{upgradeCalculation.target_plan}</p>
                </div>
              </div>
              
              <div className="bg-white p-4 rounded-lg space-y-3">
                <div className="flex justify-between items-center">
                  <span className="text-gray-600 flex items-center gap-2">
                    <Calendar className="w-4 h-4" />
                    {t('daysRemainingCurrentPlan')}
                  </span>
                  <span className="font-semibold">{upgradeCalculation.days_remaining} {t('days')}</span>
                </div>
                <div className="border-t pt-3">
                  <div className="flex justify-between items-center text-sm mb-2">
                    <span className="text-gray-600">{t('currentPlanValue')}</span>
                    <span>${upgradeCalculation.prorated_current_value.toFixed(2)}</span>
                  </div>
                  <div className="flex justify-between items-center text-sm mb-2">
                    <span className="text-gray-600">{t('targetPlanValue')}</span>
                    <span>${upgradeCalculation.prorated_target_value.toFixed(2)}</span>
                  </div>
                </div>
                <div className="border-t pt-3">
                  <div className="flex justify-between items-center">
                    <span className="text-lg font-semibold text-gray-900 flex items-center gap-2">
                      <DollarSign className="w-5 h-5 text-green-600" />
                      {t('upgradeCostPayNow')}
                    </span>
                    <span className="text-2xl font-bold text-green-600">
                      ${upgradeCalculation.upgrade_cost.toFixed(2)} {upgradeCalculation.currency}
                    </span>
                  </div>
                  <p className="text-xs text-gray-500 mt-2">
                    {t('upgradeNote')} {upgradeCalculation.days_remaining} {t('upgradeNote2')}
                  </p>
                </div>
              </div>
              
              <Button 
                onClick={() => handleUpgrade(targetUpgradePackage)}
                className="w-full bg-green-600 hover:bg-green-700 text-lg py-6"
                disabled={loading}
              >
                {loading ? (
                  <>
                    <Loader2 className="mr-2 h-5 w-5 animate-spin" />
                    {t('processing')}
                  </>
                ) : (
                  `${t('upgradeNowFor')} $${upgradeCalculation.upgrade_cost.toFixed(2)}`
                )}
              </Button>
            </CardContent>
          </Card>
        </div>
      )}

      <div className="grid md:grid-cols-2 gap-8 max-w-5xl mx-auto">{plans.map((plan) => {
          const packageKey = `${plan.id}_${billingCycle}`;
          const packageData = packages[packageKey];
          
          if (!packageData) return null;

          const isCurrentPlan = subscriptionStatus?.has_active_subscription && !subscriptionStatus?.is_trial &&
            subscriptionStatus?.subscription_type?.toLowerCase().includes(plan.id) &&
            subscriptionStatus?.subscription_type?.toLowerCase().includes(billingCycle === 'monthly' ? 'monthly' : 'yearly');

          return (
            <Card key={plan.id} className={`relative ${plan.color} ${plan.popular ? 'ring-2 ring-blue-500' : ''} flex flex-col h-full`}>
              {plan.popular && (
                <div className="absolute -top-3 left-1/2 transform -translate-x-1/2">
                  <Badge className="bg-blue-500 text-white">{t('mostPopular')}</Badge>
                </div>
              )}
              
              <CardHeader className="text-center">
                <CardTitle className="text-2xl">{plan.name}</CardTitle>
                <CardDescription>{plan.description}</CardDescription>
                <div className="mt-4">
                  {billingCycle === 'monthly' ? (
                    <>
                      <span className="text-4xl font-bold">${calculatePrice(parseFloat(getPlanPrice(plan.id)))}</span>
                      <span className="text-gray-500">{t('perMonth')}</span>
                    </>
                  ) : (
                    <>
                      <span className="text-4xl font-bold">${calculatePrice(parseFloat(getTotalYearlyPrice(plan.id)))}</span>
                      <span className="text-gray-500">{t('perYear')}</span>
                    </>
                  )}
                </div>
                {billingCycle === 'yearly' && packageData.savings && (
                  <div className="mt-2">
                    <Badge variant="default" className="bg-green-500 text-white">
                      {t('save')} {packageData.savings}
                    </Badge>
                  </div>
                )}
              </CardHeader>
              
              <CardContent className="flex-grow">
                <ul className="space-y-3">
                  {plan.features.map((feature, index) => (
                    <li key={index} className="flex items-center gap-2">
                      <Check className="h-5 w-5 text-green-500 flex-shrink-0" />
                      <span className="text-sm">{feature}</span>
                    </li>
                  ))}
                </ul>
              </CardContent>
              
              <CardFooter className="mt-auto">
                <Button 
                  className={`w-full ${isCurrentPlan ? 'bg-green-600 hover:bg-green-700 text-white' : plan.popular ? 'bg-blue-600 hover:bg-blue-700' : ''}`}
                  onClick={() => handleSubscribe(packageKey)}
                  disabled={loading}
                  data-testid={`${isCurrentPlan ? 'renew' : 'subscribe'}-${packageKey}-button`}
                >
                  {loading ? (
                    <>
                      <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                      {t('processing')}
                    </>
                  ) : isCurrentPlan ? (
                    t('renewNow')
                  ) : (
                    `${t('startPlan')} ${plan.name}`
                  )}
                </Button>
              </CardFooter>
            </Card>
          );
        })}
      </div>

      <div className="text-center mt-8 text-gray-600">
        <p>{t('allPlansIncludeText')}</p>
        <p className="text-sm">• {t('dayFreeTrialBullet')} • {t('cancelAnytimeBullet')} • {t('securePaymentsBullet')} • {t('support247Bullet')}</p>
        <p className="text-sm mt-2 text-gray-500">{t('pricesInCAD')}</p>
      </div>
    </div>
  );
};

export default Pricing;